import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { YocoProvider } from './contexts/YocoContext';
import Layout from './components/Layout';
import DashboardLayout from './components/DashboardLayout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import URLs from './pages/URLs';
import Analytics from './pages/Analytics';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import PublicAnalytics from './pages/PublicAnalytics';
import APIPage from './pages/APIPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import './App.css';

const queryClient = new QueryClient();

// Component to handle layout switching
const AppContent: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();

  // Handle scrolling behavior on route changes
  useEffect(() => {
    if (location.hash === '#contact') {
      // Handle scrolling to contact section when URL has hash
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Scroll to top for all other route changes
      window.scrollTo(0, 0);
    }
  }, [location]);
  
  // Define which routes should use the dashboard layout
  const dashboardRoutes = ['/dashboard', '/urls', '/analytics', '/billing', '/settings'];
  const isDashboardRoute = dashboardRoutes.some(route => location.pathname.startsWith(route));
  
  // Use dashboard layout for authenticated users on dashboard routes
  const shouldUseDashboardLayout = currentUser && isDashboardRoute;

  if (shouldUseDashboardLayout) {
    return (
      <DashboardLayout>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/urls" element={<URLs />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </DashboardLayout>
    );
  }

  // Special case: analytics-overview should always use the public layout
  if (location.pathname === '/analytics-overview') {
    return (
      <Layout>
        <Routes>
          <Route path="/analytics-overview" element={<PublicAnalytics />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/analytics-overview" element={<PublicAnalytics />} />
        <Route path="/api" element={<APIPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
      </Routes>
    </Layout>
  );
};

function App() {
  // Apply dark mode class to HTML element
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <YocoProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </YocoProvider>
    </QueryClientProvider>
  );
}

export default App;